// A showcase room's before/after curve, as the charts that draw it read it.
//
// One reading of `room.curves` for every surface that plots it — the
// pre-upsell's instrument (UpsellChart.tsx) and the homepage's seat card
// (homepage/listen/SeatCard.tsx) — so the two cannot disagree about which
// bands a decay is drawn at or how far the level axis reaches.

import type { CurveKind } from './content';
import type { ShowcaseRoom } from './rooms';

/** RT60 is banded data. A smooth line through it claims a resolution the
 *  solve does not have, and decay is universally reported per octave. */
export const OCTAVE_BANDS = [63, 125, 250, 500, 1000, 2000, 4000, 8000];

/** Studio spec, not a measurement — the one sanctioned hue on these charts,
 *  same reasoning as TARGET_BAND in charts/chartDraw.ts. */
export const TARGET = { freq: { lo: -3, hi: 3 }, rt60: { lo: 0.2, hi: 0.4 } };

// The dock's two gridline weights, verbatim (chartDraw.ts GRID_MAJOR/MINOR).
export const GRID_MAJOR = 'color-mix(in srgb, var(--rt-ink) 14%, transparent)';
export const GRID_MINOR = 'color-mix(in srgb, var(--rt-ink) 6%, transparent)';

export interface Point { f: number; before: number; after: number }

export interface Series {
  points: Point[];
  fLo: number;
  fHi: number;
  yLo: number;
  yHi: number;
  yStep: number;
  yMajor: number;
  unit: string;
  decimals: number;
}

/** The frequency of sample `i` of `n` on the showcase's log-spaced grid. */
export function logSpacedHz(lo: number, hi: number, i: number, n: number): number {
  return lo * (hi / lo) ** (i / (n - 1));
}

export function buildSeries(room: ShowcaseRoom, kind: CurveKind): Series {
  if (kind === 'rt60') {
    const { bands, before, after } = room.curves.rt60;
    const points = OCTAVE_BANDS
      .map((f) => {
        const i = bands.indexOf(f);
        return { f, before: before[i] ?? 0, after: after[i] ?? 0 };
      })
      .filter((p) => p.before > 0);
    return {
      points,
      fLo: OCTAVE_BANDS[0] / 1.6,
      fHi: OCTAVE_BANDS[OCTAVE_BANDS.length - 1] * 1.6,
      yLo: 0,
      yHi: Math.max(...points.map((p) => p.before)) * 1.14,
      yStep: 0.25,
      yMajor: 1,
      unit: 's',
      decimals: 2,
    };
  }
  const { lo, hi, before, after } = room.curves.freq;
  const n = before.length;
  const bound = Math.ceil(Math.max(...before.concat(after).map(Math.abs)) / 5) * 5;
  return {
    points: before.map((v, i) => ({ f: logSpacedHz(lo, hi, i, n), before: v, after: after[i] })),
    fLo: lo,
    fHi: hi,
    yLo: -bound,
    yHi: bound,
    yStep: 5,
    yMajor: 10,
    unit: 'dB',
    decimals: 0,
  };
}
